import { forwardRef, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ErrorText, Label } from 'common-ui/input/Input.styles';
import dayjs from 'dayjs';

import { StyledDatePickerWrapper } from './CustomDatePicker.styles';
import calendar from './calendar.svg';
import select from './select.svg';

const CustomDatePicker = forwardRef(
  (
    {
      label,
      value,
      onChange,
      onClear,
      placeholder,
      error,
      height,
      req,
      disableFuture,
      required = false,
      hideActionBar = false,
      slotProps: customSlotProps = {},
      clearable = true,
      ...props
    },
    ref
  ) => {
    const [cleared, setCleared] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if (cleared) {
        setCleared(false);
      }
    }, [cleared]);

    const handleClear = () => {
      setCleared(true);
      onClear?.();
      onChange(undefined);
    };

    const handleCalendarClick = () => {
      setOpen(true);
    };

    const handleDateChange = (date) => {
      if (date) {
        const isoString = dayjs(date).toISOString();
        onChange(isoString);
      } else {
        onChange(undefined);
      }
    };

    return (
      <>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ width: '100%' }}>
            {label && (
              <Label>
                {label}
                {(req || required) && <span>*</span>}
              </Label>
            )}
            <StyledDatePickerWrapper>
              <DatePicker
                disableFuture={disableFuture}
                ref={ref}
                value={value ? dayjs(value) : null}
                onChange={handleDateChange}
                sx={{
                  width: '100%',
                }}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                slotProps={{
                  field: {
                    clearable: clearable,
                    onClear: handleClear,
                    placeholder,
                  },
                  actionBar: hideActionBar
                    ? { actions: [] }
                    : {
                        actions: ['today'],
                        sx: {
                          display: 'flex',
                          justifyContent: 'flex-start',
                          padding: '8px 16px',
                        },
                      },
                  inputAdornment: {
                    sx: {
                      display: 'none',
                      zIndex: '9999999',
                    },
                  },
                  textField: {
                    onClick: handleCalendarClick,
                    InputProps: {
                      startAdornment: (
                        <img
                          className="mr-[8px] h-[14px] w-[14px]"
                          src={calendar}
                          alt="calendar"
                          style={{ cursor: 'pointer', paddingRight: '10px' }}
                        />
                      ),
                      endAdornment: (
                        <img
                          className="mr-[8px] h-[14px] w-[14px]"
                          src={select}
                          alt="select"
                          style={{ cursor: 'pointer', paddingLeft: '10px' }}
                        />
                      ),
                      sx: {
                        fontSize: '12px',
                        color: '#212529',
                        fontWeight: 500,
                        height: height ?? '38px',
                        borderRadius: '10px',
                        border: error ? '1px solid #E63946' : `none`,
                      },
                    },
                  },
                  layout: {
                    sx: {
                      marginTop: '10px',
                    },
                  },
                  ...customSlotProps,
                }}
                {...props}
              />
            </StyledDatePickerWrapper>
            {error && <ErrorText style={{ marginTop: '4px' }}>{error}</ErrorText>}
          </Box>
        </LocalizationProvider>
      </>
    );
  }
);

export default CustomDatePicker;
